/**
 * Integrytas Group - Circuit Breaker Middleware (CBM)
 * Page: CBM.BREAKERS
 *
 * Live breaker dashboard: per-service state (Closed/Open/Half-Open) with current
 * sliding-window stats, threshold/fallback configuration, manual reset/trip, and
 * a transition telemetry log. State is driven by the outbound cbmFetch wrapper.
 */

import { Fragment, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { PersonalizationProvider, PersonalizationToggle, PersonalizedGrid } from '../../components/personalization';
import type { GridColumnDef } from '../../components/personalization';
import { RelatedChildrenPanel, RelatedExpandButton, useExpandedRows, type RelatedChildGroup } from '../../components/common/RelatedChildrenPanel';
import { apiDb } from '../../lib/api_client';
import { routedInsert, routedUpdate, routedDelete } from '../../lib/routed_writes';
import type { AuthSession } from '../../types/auth';
import { StatusBadge, type StatusVariant } from '../../components/common/StatusBadge';
import { EmptyState } from '../../components/common/EmptyState';
import { ErrorBanner } from '../../components/common/ErrorBanner';
import { RowActionsMenu } from '../../components/common/RowActionsMenu';
import { FacetFilter } from '../../components/common/FacetFilter';
import { ClearFiltersButton } from '../../components/common/ClearFiltersButton';
import { hasAccess } from '../../types/auth';

const PAGE_CODE = 'CBM.BREAKERS';

interface ServiceRow {
  cbm_service_id: number; service_code: string; name: string;
  window_seconds: number; min_volume: number; failure_rate_threshold: number; timeout_threshold: number;
  timeout_ms: number; open_cooldown_seconds: number; half_open_max_trials: number; half_open_success_threshold: number;
  fallback_strategy: 'EXCEPTION' | 'LAST_CACHED' | 'STATIC_JSON'; is_active: 'Y' | 'N';
}
interface Overview {
  service_code: string; name: string; is_active: 'Y' | 'N'; state: 'CLOSED' | 'OPEN' | 'HALF_OPEN';
  fallback_strategy: string; opened_at: string | null; next_probe_at: string | null; last_transition_at: string | null;
  half_open_trials: number | null; half_open_successes: number | null;
  window_success: number; window_failure: number; window_timeout: number; window_total: number; failure_rate: number;
}
interface TransRow { cbm_transition_log_id: number; cbm_service_id: number; from_state: string | null; to_state: string; reason: string | null; failure_rate: number | null; sample_size: number | null; at: string }

const STATE_META: Record<string, { labelKey: string; label: string; variant: StatusVariant }> = {
  CLOSED:    { labelKey: 'cbm.state.closed',    label: 'Closed',    variant: 'success' },
  OPEN:      { labelKey: 'cbm.state.open',      label: 'Open',      variant: 'danger' },
  HALF_OPEN: { labelKey: 'cbm.state.half_open', label: 'Half-open', variant: 'warning' },
};

const SERVICE_GRID_COLS: GridColumnDef[] = [
  { key: 'state',                header: 'State',        defaultWidth: 110 },
  { key: 'service_code',         header: 'Service',      defaultWidth: 200 },
  { key: 'window_seconds',       header: 'Window (s)',   defaultWidth: 100 },
  { key: 'min_volume',           header: 'Min vol',      defaultWidth: 90  },
  { key: 'failure_rate_threshold', header: 'Fail rate',  defaultWidth: 90  },
  { key: 'timeout_ms',           header: 'Timeout (ms)', defaultWidth: 100 },
  { key: 'open_cooldown_seconds', header: 'Cooldown (s)', defaultWidth: 100 },
  { key: 'half_open_max_trials', header: 'Canaries',     defaultWidth: 90  },
  { key: 'fallback_strategy',    header: 'Fallback',     defaultWidth: 140 },
  { key: 'window_now',           header: 'Window now',   defaultWidth: 140 },
  { key: 'is_active',            header: 'On',           defaultWidth: 60  },
];

export interface CbmBreakersPageProps { session: AuthSession }

export function CbmBreakersPage({ session }: CbmBreakersPageProps) {
  return (
    <PersonalizationProvider pageCode="CBM.BREAKERS" session={session}>
      <CbmBreakersPageInner session={session} />
    </PersonalizationProvider>
  );
}

function CbmBreakersPageInner({ session }: CbmBreakersPageProps) {
  // §6 RBAC: csm_role_page_access decides which write affordances this user
  // gets. The server refuses the write regardless (RLS via the Common
  // Routine); this stops the page offering an action that can only error.
  const canAdd = hasAccess(session, PAGE_CODE, 'add');

  const { t } = useTranslation();
  const [services, setServices] = useState<ServiceRow[]>([]);
  const [overview, setOverview] = useState<Overview[]>([]);
  const [trans, setTrans] = useState<TransRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  // §17.B12: per-column multi-value facets; Active defaults to Active-only on first load.
  const [facets, setFacets] = useState<{ active: string[] }>({ active: ['Y'] });

  const reload = async () => {
    setBusy(true); setError(null);
    try {
      const [s, o, t] = await Promise.all([
        apiDb.from('cbm_service').select('cbm_service_id, service_code, name, window_seconds, min_volume, failure_rate_threshold, timeout_threshold, timeout_ms, open_cooldown_seconds, half_open_max_trials, half_open_success_threshold, fallback_strategy, is_active').order('service_code'),
        apiDb.rpc<Overview[]>('cbm_overview'),
        apiDb.from('cbm_transition_log').select('cbm_transition_log_id, cbm_service_id, from_state, to_state, reason, failure_rate, sample_size, at').order('at', { ascending: false }).limit(50),
      ]);
      if (s.error) throw s.error;
      setServices((s.data ?? []) as ServiceRow[]);
      setOverview((Array.isArray(o.data) ? o.data : []) as Overview[]);
      setTrans((t.data ?? []) as TransRow[]);
    } catch (err) { setError((err as Error).message); } finally { setBusy(false); }
  };
  useEffect(() => { void reload(); }, []);

  const addService = async () => {
    await routedInsert(session, 'cbm_service', { service_code: `SERVICE_${Date.now()}`, name: 'New service' });
    await reload();
  };
  const saveService = async (r: ServiceRow) => {
    const { error: e } = await routedUpdate(session, 'cbm_service', 'cbm_service_id', r.cbm_service_id, {
      service_code: r.service_code.trim(), name: r.name.trim(), window_seconds: r.window_seconds, min_volume: r.min_volume,
      failure_rate_threshold: r.failure_rate_threshold, timeout_threshold: r.timeout_threshold, timeout_ms: r.timeout_ms,
      open_cooldown_seconds: r.open_cooldown_seconds, half_open_max_trials: r.half_open_max_trials,
      half_open_success_threshold: r.half_open_success_threshold, fallback_strategy: r.fallback_strategy, is_active: r.is_active,
    });
    if (e) setError(e.message); else await reload();
  };
  const delService = async (id: number) => { await routedDelete(session, 'cbm_service', 'cbm_service_id', id); setServices(rs => rs.filter(x => x.cbm_service_id !== id)); };

  const act = async (fn: 'cbm_reset' | 'cbm_trip', code: string) => {
    setBusy(true); setError(null);
    try { const { error: e } = await apiDb.rpc(fn, { p_service_code: code }); if (e) throw e; await reload(); }
    catch (err) { setError((err as Error).message); } finally { setBusy(false); }
  };

  const ov = (code: string) => overview.find(o => o.service_code === code);

  // Related children for the expand/contract panel — the already-loaded transition
  // log rows that belong to this service (state changes over time). Labels are the
  // resolved state transition; the timestamp/reason rides along as hover title.
  const detail = useExpandedRows<number>();
  const buildRelatedGroups = (id: number): RelatedChildGroup[] => [
    {
      key: 'transitions',
      table: 'cbm_transition_log', pkColumn: 'cbm_transition_log_id',
      title: t('cbm.related.transitions', 'Transitions'),
      items: trans
        .filter(x => x.cbm_service_id === id)
        .map(x => ({
          id: x.cbm_transition_log_id,
          label: `${x.from_state ?? '—'} → ${x.to_state}`,
          title: `${new Date(x.at).toLocaleString()}${x.reason ? ` · ${x.reason}` : ''}`,
        })),
    },
  ];
  const hasRelated = (id: number) => buildRelatedGroups(id).some(g => g.items.length > 0);

  const facetOptions = useMemo(() => ({
    active: [
      { value: 'Y', label: t('common.boolean.active', 'Active') },
      { value: 'N', label: t('common.boolean.inactive', 'Inactive') },
    ],
  }), [t]);

  const anyFilterActive = filter.trim().length > 0 || facets.active.length > 0;
  const clearAll = () => { setFilter(''); setFacets({ active: [] }); };

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const matchesText = (r: ServiceRow) => !q || r.service_code.toLowerCase().includes(q) || (r.name ?? '').toLowerCase().includes(q);
    const matchesActive = (r: ServiceRow) => facets.active.length === 0 || facets.active.includes(r.is_active);
    return services.filter(r => matchesText(r) && matchesActive(r));
  }, [services, filter, facets]);

  return (
    <div className="flex h-full flex-col">
      <div className="flex h-10 shrink-0 items-center gap-2 border-b border-csm-border bg-white px-3">
        <span className="csm-page-title">{t('cbm.title', 'Circuit Breakers')}</span>
        <input
          type="text"
          value={filter}
          onChange={e => setFilter(e.target.value)}
          placeholder={t('cbm.placeholder.filter', 'Filter services…')}
          className="h-7 w-60 rounded border border-csm-border px-2 text-csm-md focus:border-csm-watermark focus:outline-none"
        />
        <FacetFilter label={t('common.boolean.active', 'Active')} options={facetOptions.active} selected={facets.active} onChange={v => setFacets(f => ({ ...f, active: v }))} />
        <ClearFiltersButton active={anyFilterActive} onClear={clearAll} label={t('common.clear_filters', 'Clear filters')} />
        <button onClick={() => void reload()} className="h-7 rounded border border-csm-border bg-white px-2 text-csm-sm hover:bg-csm-surface-alt">{t('cbm.refresh', 'Refresh')}</button>
        {canAdd && (<button onClick={() => void addService()} className="h-7 rounded border border-csm-border bg-white px-2 text-csm-sm hover:bg-csm-surface-alt">{t('cbm.addService', '+ Service')}</button>)}
        {busy && <span className="text-csm-xs text-csm-watermark">{t('cbm.working', 'Working…')}</span>}
        <div className="ml-auto"><PersonalizationToggle /></div>
      </div>

      <div className="flex-1 overflow-auto p-3">
        {error && (
          <ErrorBanner
            message={t('cbm.load_failed', "Couldn't load circuit breakers.")}
            detail={error}
            retry_label={t('common.retry', 'Retry')}
            on_retry={() => void reload()}
            details_label={t('common.details', 'Details')}
          />
        )}
        <Section title={t('cbm.section.services', 'Registered Services')}>
          <div className="overflow-x-auto">
            <PersonalizedGrid componentKey="grid" columns={SERVICE_GRID_COLS}>
              {(visibleCols, widths) => (
                <table className="w-max border-collapse text-csm-sm">
                  <thead><tr className="border-b border-csm-border text-left text-csm-2xs uppercase tracking-wide text-csm-watermark">
                    <th className="w-7 px-2 py-1" title={t('cbm.related.header', 'Related transitions')} />
                    {visibleCols.map(col => (
                      <th key={col.key} className="px-2 py-1"
                          style={widths[col.key] ? { width: widths[col.key] } : undefined}>{t(`cbm.col.${col.key}`, col.header)}</th>
                    ))}
                    <th className="px-2 py-1"></th>
                  </tr></thead>
                  <tbody>
                    {filtered.map(r => {
                      const i = services.indexOf(r);
                      const o = ov(r.service_code);
                      return (
                        <Fragment key={r.cbm_service_id}>
                        <tr className="border-b border-csm-border-soft align-top">
                          <td className="px-2 py-1 text-center align-top" onClick={e => e.stopPropagation()}>
                            <RelatedExpandButton
                              open={detail.isOpen(r.cbm_service_id)}
                              hasChildren={hasRelated(r.cbm_service_id)}
                              onToggle={() => detail.toggle(r.cbm_service_id)}
                              expandLabel={t('cbm.related.expand', 'Show related records')}
                              collapseLabel={t('cbm.related.collapse', 'Hide related')}
                              tooltip={t('cbm.related.tooltip', 'Show related transitions')}
                            />
                          </td>
                          {visibleCols.map(col => (
                            <td key={col.key} className="px-2 py-1"
                                style={widths[col.key] ? { width: widths[col.key] } : undefined}>
                              {col.key === 'state' && <>
                                {(() => {
                                  const st = o?.state ?? 'CLOSED';
                                  const meta = STATE_META[st];
                                  return (
                                    <StatusBadge
                                      label={meta ? t(meta.labelKey, meta.label) : st.replace('_', '-')}
                                      variant={meta?.variant ?? 'neutral'}
                                    />
                                  );
                                })()}
                                {o?.state === 'OPEN' && o.next_probe_at && <div className="mt-0.5 text-csm-2xs text-csm-watermark">{t('cbm.probe', 'probe {{time}}', { time: new Date(o.next_probe_at).toLocaleTimeString() })}</div>}
                              </>}
                              {col.key === 'service_code' && <>
                                <input value={r.service_code} onChange={e => setServices(rs => rs.map((x, j) => j === i ? { ...x, service_code: e.target.value } : x))} onBlur={() => void saveService(services[i])} className="h-7 w-44 rounded border border-csm-border px-1 font-mono text-csm-xs" />
                                <input value={r.name} onChange={e => setServices(rs => rs.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} onBlur={() => void saveService(services[i])} className="mt-0.5 h-6 w-44 rounded border border-csm-border-soft px-1 text-csm-2xs" />
                              </>}
                              {col.key === 'window_seconds' && <Num v={r.window_seconds} on={v => setServices(rs => rs.map((x, j) => j === i ? { ...x, window_seconds: v } : x))} blur={() => void saveService(services[i])} />}
                              {col.key === 'min_volume' && <Num v={r.min_volume} on={v => setServices(rs => rs.map((x, j) => j === i ? { ...x, min_volume: v } : x))} blur={() => void saveService(services[i])} />}
                              {col.key === 'failure_rate_threshold' && <Num step="0.01" v={r.failure_rate_threshold} on={v => setServices(rs => rs.map((x, j) => j === i ? { ...x, failure_rate_threshold: v } : x))} blur={() => void saveService(services[i])} />}
                              {col.key === 'timeout_ms' && <Num v={r.timeout_ms} on={v => setServices(rs => rs.map((x, j) => j === i ? { ...x, timeout_ms: v } : x))} blur={() => void saveService(services[i])} />}
                              {col.key === 'open_cooldown_seconds' && <Num v={r.open_cooldown_seconds} on={v => setServices(rs => rs.map((x, j) => j === i ? { ...x, open_cooldown_seconds: v } : x))} blur={() => void saveService(services[i])} />}
                              {col.key === 'half_open_max_trials' && <Num v={r.half_open_max_trials} on={v => setServices(rs => rs.map((x, j) => j === i ? { ...x, half_open_max_trials: v } : x))} blur={() => void saveService(services[i])} />}
                              {col.key === 'fallback_strategy' && (
                                <select value={r.fallback_strategy} onChange={e => { setServices(rs => rs.map((x, j) => j === i ? { ...x, fallback_strategy: e.target.value as ServiceRow['fallback_strategy'] } : x)); }} onBlur={() => void saveService(services[i])} className="h-7 rounded border border-csm-border px-1 text-csm-2xs">
                                  {['EXCEPTION', 'LAST_CACHED', 'STATIC_JSON'].map(s => <option key={s} value={s}>{s}</option>)}
                                </select>
                              )}
                              {col.key === 'window_now' && (
                                <span className="text-csm-2xs text-csm-ink-soft">{o ? <>{t('cbm.windowNow.reqs', '{{n}} reqs', { n: o.window_total })} · <span className={o.failure_rate >= r.failure_rate_threshold ? 'text-csm-danger' : ''}>{t('cbm.windowNow.fail', '{{pct}}% fail', { pct: Math.round(o.failure_rate * 100) })}</span></> : '—'}</span>
                              )}
                              {col.key === 'is_active' && <input type="checkbox" checked={r.is_active === 'Y'} onChange={e => { setServices(rs => rs.map((x, j) => j === i ? { ...x, is_active: e.target.checked ? 'Y' : 'N' } : x)); }} onBlur={() => void saveService(services[i])} />}
                            </td>
                          ))}
                          <td className="px-2 py-1 whitespace-nowrap">
                            <button onClick={() => void act('cbm_reset', r.service_code)} className="mr-1 text-csm-xs text-primary hover:underline">{t('cbm.action.reset', 'reset')}</button>
                            <RowActionsMenu
                              label={t('common.row_actions', 'Row actions')}
                              actions={[
                                { key: 'trip', label: t('cbm.action.trip', 'trip'), onSelect: () => void act('cbm_trip', r.service_code), danger: true },
                                { key: 'del', label: t('cbm.action.del', 'del'), onSelect: () => void delService(r.cbm_service_id), danger: true },
                              ]}
                            />
                          </td>
                        </tr>
                        {detail.isOpen(r.cbm_service_id) && (
                          <tr>
                            <td colSpan={visibleCols.length + 2} className="border-b border-csm-border-soft bg-csm-surface-alt/40 px-3 py-3" onClick={e => e.stopPropagation()}>
                              <RelatedChildrenPanel groups={buildRelatedGroups(r.cbm_service_id)} session={session} />
                            </td>
                          </tr>
                        )}
                        </Fragment>
                      );
                    })}
                    {filtered.length === 0 && <tr><td colSpan={visibleCols.length + 2} className="px-2 py-3"><EmptyState variant={anyFilterActive ? 'no-match' : 'no-data'} title={t('cbm.empty.services', 'No services registered.')} action={anyFilterActive ? undefined : canAdd && (<button onClick={() => void addService()} className="h-7 rounded border border-csm-border bg-white px-2 text-csm-sm hover:bg-csm-surface-alt">{t('cbm.addService', '+ Service')}</button>)} /></td></tr>}
                  </tbody>
                </table>
              )}
            </PersonalizedGrid>
          </div>
        </Section>

        <Section title={t('cbm.section.transitions', 'Transition Log')}>
          <table className="w-full border-collapse text-csm-sm">
            <tbody>
              {trans.map(t => {
                const svcName = services.find(s => s.cbm_service_id === t.cbm_service_id)?.name ?? '—';
                return (
                  <tr key={t.cbm_transition_log_id} className="border-b border-csm-border-soft">
                    <td className="px-2 py-1 text-csm-2xs text-csm-watermark">{new Date(t.at).toLocaleString()}</td>
                    <td className="px-2 py-1 text-csm-xs">{svcName}</td>
                    <td className="px-2 py-1 text-csm-xs">{t.from_state ?? '—'} → <b>{t.to_state}</b></td>
                    <td className="px-2 py-1 text-csm-2xs text-csm-ink-soft">{t.reason}{t.failure_rate != null ? ` · ${Math.round(t.failure_rate * 100)}% over ${t.sample_size}` : ''}</td>
                  </tr>
                );
              })}
              {trans.length === 0 && <tr><td className="px-2 py-2"><EmptyState variant="no-data" title={t('cbm.empty.transitions', 'No transitions yet.')} /></td></tr>}
            </tbody>
          </table>
        </Section>
      </div>
    </div>
  );
}

function Num({ v, on, blur, step }: { v: number; on: (v: number) => void; blur: () => void; step?: string }): ReactNode {
  return <input type="number" step={step} value={v} onChange={e => on(Number(e.target.value))} onBlur={blur} className="h-7 w-16 rounded border border-csm-border px-1 text-csm-xs" />;
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="mb-5">
      <h3 className="mb-2 text-csm-sm font-semibold text-csm-ink">{title}</h3>
      {children}
    </section>
  );
}
